import { Link } from 'react-router-dom';
import { ServerIcon } from '@heroicons/react/24/outline';
import { useDeleteMcpConnection } from '../../hooks/useMcpConnections';
import { McpConnection } from '../../types';
import DeleteResourceControl from '../shared/DeleteResourceControl';

interface McpConnectionCardProps {
  connection: McpConnection;
  agentCount?: number;
}

export default function McpConnectionCard({ connection, agentCount = 0 }: McpConnectionCardProps) {
  const deleteMcpConnection = useDeleteMcpConnection();

  const getAuthTypeBadgeClasses = (authType: string) => {
    switch (authType) {
      case 'bearer':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'api-key':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      case 'basic':
        return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getAuthTypeLabel = (authType: string) => {
    switch (authType) {
      case 'bearer':
        return 'Bearer';
      case 'api-key':
        return 'API Key';
      case 'basic':
        return 'Basic';
      default:
        return 'None';
    }
  };

  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-200">
      <Link to={`/mcp-connections/${connection.id}`} className="block">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <ServerIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 truncate">
                {connection.name}
              </h3>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 font-mono truncate">
              {connection.url}
            </p>
            {connection.description && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                {connection.description}
              </p>
            )}
            <div className="mt-3 flex items-center flex-wrap gap-2">
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  connection.isActive
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                }`}
              >
                {connection.isActive ? 'Active' : 'Inactive'}
              </span>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getAuthTypeBadgeClasses(
                  connection.authType
                )}`}
              >
                {getAuthTypeLabel(connection.authType)}
              </span>
              {agentCount > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                  {agentCount} {agentCount === 1 ? 'agent' : 'agents'}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Created {new Date(connection.createdAt).toLocaleDateString()}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Updated {new Date(connection.updatedAt).toLocaleDateString()}
          </div>
        </div>
      </Link>

      {/* One delete affordance for the whole app (task 864): the flag, the
          dialog, the dependents preview, the mutation and the toast in one
          place. The inline two-step swap this replaces said nothing about
          what else used the resource, and offered no way back. */}
      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <DeleteResourceControl
          flag="innovate.fe.mcp-connections.delete"
          entity="mcp-connections"
          entityLabel="MCP connection"
          name={connection.name}
          id={connection.id}
          stopPropagation
          body={<p>Agents attached to it stop reaching its tools.</p>}
          onDelete={() => deleteMcpConnection.mutateAsync(connection.id)}
        />
      </div>
    </div>
  );
}
